import "react-toastify/dist/ReactToastify.css";

import { useCallback, useEffect, useState } from "react";

import { Spin } from "antd";
import axios from "axios";
import { ethers } from "ethers";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import ClaimTopicsPage from "./components/ClaimTopicsPage.jsx";
import CreateClaimTopic from "./components/CreateClaimTopic.jsx";
import CreateDigitalId from "./components/CreateDigitalId.jsx";
import CreateTrustedIssuer from "./components/CreateTrustedIssuer.jsx";
import CredentialsCodePage from "./components/CredentialsCodePage";
import DigitalIdentityDetailView from "./components/DigitalIdentityDetailPage.jsx";
import EditClaims from "./components/EditClaims.jsx";
import EditClaimsSummaryView from "./components/EditClaimsSummaryView.jsx";
import EditClaimTopic from "./components/EditClaimTopic.jsx";
import ForgotPasswordPage from "./components/ForgotPasswordPage.jsx";
import Home from "./components/Home.jsx";
import IdentitiesPage from "./components/IdentitiesPage.jsx";
import Layout from "./components/Layout";
import Login from "./components/LoginPage.jsx";
import NavBar from "./components/NavBar.jsx";
import Protected from "./components/Protected";
import TrustedIssuersPage from "./components/TrustedIssuersPage.jsx";
import ViewClaimTopic from "./components/ViewClaimTopic";
import { RoleContext } from "./context/RoleContext";
import BlockchainService from "./services/BlockchainService.js";
import { endSession } from "./services/endSession";
import parseInitialize from "./services/parseInitialize";
import { admitToAdminUi, restoreStoredSession } from "./services/sessionRestore";
import { endSessionOnce, onSessionLost, recordSignOutReason } from "./services/tokenRenewal";
import AutoLogout from "./utils/AutoLogout";

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [blockchainService, setBlockchainService] = useState<BlockchainService | null>(null);
  const [user, setUser] = useState(null); // State to hold user
  const [role, setRole] = useState<any>([]);
  const [dfnsToken, setDfnsToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true); // New state for initialization
  //let provider: ethers.Provider | null = null;
  let [provider, setProvider] = useState<ethers.Provider | null>(null);
  // Define the getToken function
  const getToken = async (request: any) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_PARSE_SERVER_URL}/auth/login`, request);
      const data = response.data;
      return {
        token: data?.access_token || "",
        jwt: data?.jwt || "",
        refreshToken: data?.refreshToken || "",
        roles: data?.user?.roles || [],
        user: data?.user,
        dfnsToken: data?.dfns_token,
      };
    } catch (error) {
      console.log("Error during authentication:", error);
      return {
        token: "",
        jwt: "",
        refreshToken: "",
        roles: [],
        user: null,
        dfnsToken: null,
      };
    }
  };

  // Abstracted function to initialize BlockchainService
  const initializeBlockchainService = (provider: ethers.Provider) => {
    console.log("Initializing BlockchainService...");
    const _blockchainService = new BlockchainService(
      provider,
      process.env.REACT_APP_HARDHAT_CONTRACT_ADDRESS || "",
      process.env.REACT_APP_HARDHAT_IDENTITY_FACTORY_ADDRESS || ""
    );
    setBlockchainService(_blockchainService);
  };

  // Define the onLogout function for email/password login
  const onLogoutEmailPassword = useCallback(async (reason?: "idle" | "session") => {
    // Destroying the session is the whole of it: the access token is bound to the session
    // and refused once it is gone. Revoking the JWT separately first cost more than it
    // gave — with an expired bearer it set off a renewal, a wasted rotation that also slid
    // the session it was about to end, and then failed regardless.
    await endSession();

    // Reset client-side state
    setRole([]);
    setDfnsToken(null);
    setUser(null);
    setIsConnected(false);
    setBlockchainService(null);

    // Left for the login page rather than toasted: a toast is gone in seconds, and the
    // person who was signed out for inactivity is by definition not at the screen.
    // A refused renewal knows a truer reason than inactivity.
    if (reason === "session") recordSignOutReason();
    else if (reason === "idle") recordSignOutReason("idle");
    else toast.success("Logged out successfully.");
  }, []);

  // Manual and idle logout go through the interceptor's once-guard: calls still in flight
  // when the session goes are refused with 209 and would start a second teardown that
  // overwrites their reason. The onSessionLost handler below calls onLogoutEmailPassword
  // directly; it is already guarded.
  const logOut = (reason?: "idle") => endSessionOnce(() => onLogoutEmailPassword(reason));

  // Define the onLogin function (Username/Password Login)
  const onLogin = async (email: string, password: string) => {
    const { token, jwt, refreshToken, roles, user, dfnsToken } = await getToken({ email, password });

    // A session and a bearer are what a good login returns. The row's roles are no test
    // of that: an account whose roles live only in _Role has none there, and the whoami
    // check below is what decides access.
    if (token && jwt) {
      // Store the session token AND the auth JWT BEFORE parseInitialize(),
      // because parseInitialize() reads `authJwt` from localStorage to install
      // the `Authorization: Bearer` header that the backend `withAuth` checks.
      // Storing it after (the previous order) left gated cloud calls such as
      // setUserStatus (identity move across tabs) tokenless → auth error.
      localStorage.setItem("sessionToken", token);
      localStorage.setItem("authJwt", jwt);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
      await parseInitialize();

      const resolvedRoles = await admitToAdminUi(roles);
      if (!resolvedRoles) {
        toast.error("This account has no Admin UI access (needs CentralAuthority, admin, TrustedIssuer, or reviewer).");
        return;
      }

      setRole([...resolvedRoles]);
      setUser(user);
      setDfnsToken(dfnsToken);
      setIsConnected(true);
      //const provider = await setupProvider();
      if (!provider) {
        console.error("❌ Failed to initialize provider.");
        toast.error("Could not initialize provider. Please try again.");
        return;
      }

      console.log("✅ Provider initialized:", provider);
      initializeBlockchainService(provider);
    } else {
      toast.error("Incorrect username / password");
    }
  };

  useEffect(() => {
    setupProvider()
      .then(setProvider)
      .catch((error) => console.error("Failed to set up provider:", error));
  }, []);

  // The interceptor lives outside React, so it hands the teardown back here.
  useEffect(() => {
    // Returned, not voided: endSession holds off re-entry until the teardown settles.
    onSessionLost(() => onLogoutEmailPassword("session"));
  }, [onLogoutEmailPassword]);

  async function setupProvider(): Promise<ethers.Provider | null> {
    // DFNS-only project — no flow ever signs via a connected browser wallet.
    // Always use the read-only RPC provider so BlockchainService takes its
    // existing "no signer" branch unconditionally. A window.ethereum-based
    // Web3Provider used to be tried first here, but a wallet extension merely
    // being installed (not even connected) was enough for BlockchainService to
    // attempt signer.getAddress() on reads that never needed a signer — e.g.
    // getIdentity() during CreateDigitalId's post-creation poll — throwing
    // "unknown account #0" (UNSUPPORTED_OPERATION) right between
    // IdentityCreated__e and IdentityAdded__e.
    let newProvider: ethers.Provider | null = null;
    console.log("🔍 Setting up RPC provider...");

    const rpcUrl = process.env.REACT_APP_RPC_URL;
    if (rpcUrl) {
      // staticNetwork skips ethers' eth_chainId detection before every request: it uses
      // the configured chain id, or detects it once when none is set. A raw `Infinity`
      // polling interval gets coerced by setInterval into firing near-instantly (that
      // hammered eth_getLogs), so keep a real, deliberately slow one.
      const chainId = Number(process.env.REACT_APP_HARDHAT_CHAIN_ID);
      newProvider = new ethers.JsonRpcProvider(rpcUrl, chainId ? { name: "unknown", chainId } : undefined, {
        staticNetwork: true,
        pollingInterval: 30000,
      });
    }

    if (newProvider) {
      setProvider(newProvider);
      return newProvider;
    }

    console.error("❌ No provider available! Set REACT_APP_RPC_URL.");
    return null;
  }

  useEffect(() => {
    void restoreStoredSession()
      .then((restored) => {
        if (!restored) return;
        setRole(restored.roles);
        setUser(restored.user);
        setDfnsToken(restored.dfnsToken);
        setIsConnected(true);
      })
      .finally(() => setInitializing(false));
  }, []);

  // Handle loading state when roles change
  useEffect(() => {
    if (role.length > 0) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [role]);

  useEffect(() => {
    if (provider && isConnected) {
      initializeBlockchainService(provider);
    }
  }, [provider, isConnected]);

  if (initializing) {
    return (
      <div className="z-50 h-screen w-screen flex justify-center items-center">
        <Spin />
      </div>
    );
  }

  return (
    <RoleContext.Provider value={{ role, setRole, dfnsToken, setDfnsToken, user, setUser }}>
      {/* Loading Spinner Overlay */}
      {loading && (
        <div className="z-50 h-screen w-screen overflow-hidden absolute top-0 left-0 flex justify-center items-center bg-[#00000040]">
          <Spin />
        </div>
      )}
      <Router>
        <AutoLogout enabled={role.length > 0} onIdle={() => logOut("idle")} />
        {/* Navigation Bar (Only visible when logged in) */}
        {role.length > 0 && (
          <div className={`topnav p-0`}>
            <NavBar onLogout={() => logOut()} />
          </div>
        )}

        <Layout>
          <div className={`${role.length === 0 ? "p-0 -ml-4 overflow-hidden" : "content"}`}>
            <ToastContainer
              position="top-right"
              className="toast-background"
              progressClassName="toast-progress-bar"
              autoClose={4000}
              closeOnClick
              pauseOnHover
            />
            {/* Application Routes */}
            <Routes>
              <Route
                path="/"
                element={
                  <Protected roles={role}>
                    <Home />
                  </Protected>
                }
              />
              <Route path="/login" element={<Login onLogin={onLogin} />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              {/* The link in a new admin's password setup email; same code-then-password card as forgot password. */}
              <Route path="/create-password" element={<ForgotPasswordPage mode="create" />} />
              <Route path="/credentials/code" element={<CredentialsCodePage />} />

              <Route
                path="/topics"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <ClaimTopicsPage service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/topics/create"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <CreateClaimTopic service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/topics/edit/:topicId"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <EditClaimTopic service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/topics/:topicId"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <ViewClaimTopic service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/issuers"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <TrustedIssuersPage service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/issuers/create"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <CreateTrustedIssuer service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/issuers/:issuerId"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <CreateTrustedIssuer service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/identities"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <IdentitiesPage service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/identities/create"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <CreateDigitalId service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/identities/:identityId/:userId?"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <DigitalIdentityDetailView service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/identities/:identityId/edit"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <EditClaims service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
              <Route
                path="/identities/:identity/edit/summary"
                element={
                  <Protected roles={role}>
                    {blockchainService ? (
                      <EditClaimsSummaryView service={blockchainService} />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <Spin tip="Initializing service..." />
                      </div>
                    )}
                  </Protected>
                }
              />
            </Routes>
          </div>
        </Layout>
      </Router>
    </RoleContext.Provider>
  );
}

export default App;
